import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 🎯 Database schema helper functions
export const db = {
  // Users
  async getUsers() {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async getUserById(id) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  async createUser(userData) {
    console.log('Creating user in database:', userData);
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single();
    console.log('User creation result:', { data, error });
    return { data, error };
  },

  async updateUser(id, updates) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async deleteUser(id) {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Reports
  async getReports() {
    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        user:users!user_id(username),
        agent:users!agent_id(username)
      `)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async getReportsByAgent(agentId) {
    console.log('Getting reports for agent:', agentId);
    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        user:users!user_id(username),
        agent:users!agent_id(username)
      `)
      .eq('agent_id', agentId)
      .order('created_at', { ascending: false });
    console.log('Agent reports result:', { data, error });
    return { data, error };
  },

  async getReportsByUser(userId) {
    console.log('Getting reports for user:', userId);
    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        user:users!user_id(username),
        agent:users!agent_id(username)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    console.log('User reports result:', { data, error });
    return { data, error };
  },

  async createReport(reportData) {
    console.log('Creating report with data:', reportData);
    const { data, error } = await supabase
      .from('reports')
      .insert([reportData])
      .select(`
        *,
        user:users!user_id(username),
        agent:users!agent_id(username)
      `)
      .single();
    console.log('Report creation result:', { data, error });
    return { data, error };
  },

  async updateReport(id, updates) {
    const { data, error } = await supabase
      .from('reports')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        user:users!user_id(username),
        agent:users!agent_id(username)
      `)
      .single();
    return { data, error };
  },

  async deleteReport(id) {
    const { error } = await supabase
      .from('reports')
      .delete()
      .eq('id', id);
    return { error };
  },

  // News
  async getNews() {
    const { data, error } = await supabase
      .from('news')
      .select(`
        *,
        agent:users!agent_id(username)
      `)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async getNewsByAgent(agentId) {
    const { data, error } = await supabase
      .from('news')
      .select('*')
      .eq('agent_id', agentId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async createNews(newsData) {
    const { data, error } = await supabase
      .from('news')
      .insert([newsData])
      .select()
      .single();
    return { data, error };
  },

  async updateNews(id, updates) {
    const { data, error } = await supabase
      .from('news')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  async deleteNews(id) {
    const { error } = await supabase
      .from('news')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Auth
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  async signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });
    return { data, error };
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  // Real-time subscriptions
  subscribeToReports(callback) {
    return supabase
      .channel('reports_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'reports' 
        }, 
        callback
      )
      .subscribe();
  },

  subscribeToNews(callback) {
    return supabase
      .channel('news_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'news' 
        }, 
        callback
      )
      .subscribe();
  }
};